import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.addEventListener('click', (event) => {
      const botao = event.target.closest('[data-ambiente-editar]');
      if (!botao) return;
      const linha = botao.closest('[data-line]');
      if (!linha) return;

      // No drawer, #editarAmbiente troca a pill pelo <input> de forma síncrona
      // dentro deste mesmo clique. O #handleClick do dialog-component (bubbling,
      // assets/dialog.js:121) roda depois e usa isClickedOutside → dialog.contains
      // (assets/utilities.js:318) para decidir se fecha o drawer; como o botão
      // clicado já foi desconectado do DOM quando esse handler avalia, contains()
      // dá falso e o clique é tratado como "fora do dialog" — o drawer fecha
      // antes do input aparecer. stopPropagation aqui barra a subida do evento.
      event.stopPropagation();
      this.#editarAmbiente(linha);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', { injectStylesheet: true });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Renomeia o ambiente de uma linha.
   * @param {Object} config
   * @param {number} config.line - índice 1-based da linha
   * @param {Object} config.properties - properties ATUAIS da linha
   * @param {string} config.ambiente - novo valor, já normalizado e sem colisão
   * @param {HTMLInputElement} [config.input] - input em edição, para restaurar em caso de erro
   * @param {HTMLElement} [config.atual] - pill original, para restaurar em caso de erro
   */
  updateAmbiente(config) {
    const { line, properties, ambiente, input, atual } = config;

    // /cart/change.js SUBSTITUI o objeto de properties inteiro. Mandar só
    // Ambiente apagaria 'Altura desejada', 'Abertura' e as ocultas _modelo,
    // _calc_v e _fator — a planilha de produção e theme/pedidos/Código.js
    // deixariam de reconhecer o pedido. Por isso o merge sobre as atuais.
    const novas = Object.assign({}, properties);
    if (ambiente) novas['Ambiente'] = ambiente;
    else delete novas['Ambiente'];

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      properties: novas,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    // Erro de validação ou de rede não pode deixar o <input> encalhado no
    // lugar da pill até um reload — restaura a pill original, igual ao Esc.
    const restaurar = () => { if (input && atual && input.isConnected) input.replaceWith(atual); };

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = JSON.parse(responseText);
        if (parsed.errors) { console.error(parsed.errors); restaurar(); return; }
        morphSection(
          this.sectionId,
          parsed.sections[this.sectionId],
          this.isDrawer ? 'hydration' : 'full',
          { injectStylesheet: true }
        );
      })
      .catch((error) => { console.error(error); restaurar(); });
  }

  /**
   * O filtro `json` do Liquid serializa item.properties como array de pares
   * [[chave, valor], ...], não como objeto — mesmo com data-properties vindo
   * de `item.properties | json`. Sem essa normalização, properties['Ambiente']
   * é sempre undefined e o merge do updateAmbiente manda um objeto quebrado.
   */
  #propsDaLinha(el) {
    const parsed = JSON.parse(el.dataset.properties || '[]');
    return Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed;
  }

  #editarAmbiente(linha) {
    const A = window.PromacalAmbiente;
    // Sem ambiente-tag.js carregado, não tem MAX/proximoNome pra montar o input
    // de edição. Sair aqui: a pill fica intacta (degrada em silêncio, não quebra).
    if (!A) return;
    const atual = linha.querySelector('[data-ambiente-editar]');
    const properties = this.#propsDaLinha(linha);
    const line = parseInt(linha.dataset.line, 10);

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'cart-items__ambiente-input';
    input.maxLength = A.MAX;
    input.value = properties['Ambiente'] || '';
    atual.replaceWith(input);
    input.focus();
    input.select();

    // No drawer a linha vive dentro de um <dialog> modal nativo (showModal).
    // O Escape sobe por bubbling até o #handleKeyDown do dialog-component
    // (assets/dialog.js:64), que chama closeDialog() — stopPropagation no
    // keydown do input barra essa subida e mantém o drawer aberto ao
    // cancelar a edição. (Existiu aqui uma segunda camada via evento
    // 'cancel' do <dialog>, removida na revisão: destravar() rodava
    // síncrono dentro deste mesmo handler de keydown, e o close request do
    // <dialog> só é sinalizado depois que o dispatch do keydown termina —
    // o listener 'cancel' já tinha sido removido quando poderia disparar.
    // Código morto que, além de não fazer nada, vazava se o input fosse
    // destruído por um morph de outra origem, ex. #handleCartUpdate, sem
    // passar por Enter/Esc: o listener 'cancel' ficava preso no <dialog>
    // e, por causa do seu preventDefault, o Esc parava de fechar o drawer.)

    const confirmar = () => {
      input.removeEventListener('blur', confirmar);

      // Renomear pode FUNDIR linhas: se este virar 'Quarto' e já existir um
      // 'Quarto' na mesma variante, a Shopify soma as quantidades e uma
      // identificação some sem aviso. proximoNome contra as outras linhas evita.
      const outros = Array.from(this.querySelectorAll('[data-line]'))
        .filter((el) => el !== linha)
        .map((el) => this.#propsDaLinha(el)['Ambiente'])
        .filter(Boolean);

      const valor = A.proximoNome(A.normalizar(input.value), outros);
      this.updateAmbiente({ line, properties, ambiente: valor, input, atual });
    };

    input.addEventListener('blur', confirmar);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        input.removeEventListener('blur', confirmar);
        input.replaceWith(atual);
      }
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
